import moment from 'moment';
import api from '../api';

function calculatePrice(periods, price, discounts, coupon = false) {
  const totalPeriods = Object.values(periods).flatMap(item => item).length;
  const totalWithoutDiscount = totalPeriods * price;
  let discount = 0;
  discounts.forEach(item => {
    if (totalPeriods >= Number(item.period)) {
      discount = item.value;
    }
  });

  if(coupon)
    discount = coupon.discount;

  const totalDiscount = (totalWithoutDiscount * discount) / 100;
  const totalWithDiscount = totalWithoutDiscount - totalDiscount;
  return { totalWithDiscount, totalWithoutDiscount, totalDiscount, discount, totalPeriods };
}

function savePixTempTransactionId(id) {
  if (id) sessionStorage.setItem(`pix_transcation_id`, id);
}
function getPixTempTransactionId() {
  return sessionStorage.getItem(`pix_transcation_id`) || undefined;
}

function savePixCode(id, data) {
  sessionStorage.setItem(`pix-${id}`, JSON.stringify(data));
  sessionStorage.setItem(`pix-${id}-expires`, moment().add(10, 'minutes'));
}
function getPixCode(id) {
  return JSON.parse(sessionStorage.getItem(`pix-${id}`)) || undefined;
}
function getPixCodeExpireTime(id) {
  return sessionStorage.getItem(`pix-${id}-expires`) || undefined;
}

function removePixSession(id) {
  sessionStorage.removeItem(`pix-${id}`);
  sessionStorage.removeItem(`pix_transcation_id`);
}

export default {
  calculatePrice,
  savePixCode,
  getPixCode,
  getPixCodeExpireTime,
  savePixTempTransactionId,
  getPixTempTransactionId,
  removePixSession,
};

// V5 Payment Flow Helper Functions
export const paymentV5 = {
  /**
   * Create customer for V5 API
   * @param {Object} userProfile - User profile data
   * @param {Object} financialData - Financial data
   */
  createCustomer: async (userProfile, financialData) => {
    const customerData = {
      name: userProfile.name || `${userProfile.first_name} ${userProfile.last_name}`.trim(),
      email: userProfile.email,
      document: userProfile.document_id || financialData.document_id,
      document_type: 'CPF',
      type: 'individual',
      phone: {
        country_code: '55',
        area_code: userProfile.phone?.substr(0, 2) || '11',
        number: userProfile.phone?.substr(2) || '999999999'
      },
      address: financialData.address && {
        line_1: `${financialData.address.street}, ${financialData.address.number}`,
        line_2: financialData.address.complement || '',
        zip_code: financialData.address.zipcode?.replace(/\D/g, ''),
        city: financialData.address.city,
        state: financialData.address.state,
        country: 'BR'
      }
    };

    return api.createCustomerV5(customerData);
  },

  /**
   * Create credit card for V5 API
   * @param {string} customerId - Customer ID
   * @param {Object} cardData - Credit card data
   */
  createCreditCard: async (customerId, cardData) => {
    const creditCardData = {
      number: cardData.number,
      holder_name: cardData.holder_name,
      holder_document: cardData.holder_document,
      exp_month: cardData.exp_month,
      exp_year: cardData.exp_year,
      cvv: cardData.cvv,
      brand: cardData.brand,
      billing_address: cardData.billing_address,
      options: {
        verify_card: true
      }
    };

    return api.createCreditCardV5(customerId, creditCardData);
  },

  /**
   * Create order for V5 API
   * @param {Object} orderData - Order data including profile, booking, and financial info
   * @param {number} installments - Number of installments
   */
  createOrder: async (orderData, installments = 1) => {
    const { profile, booking, financial } = orderData;
    
    const order = {
      customer: {
        name: profile.name || `${profile.first_name} ${profile.last_name}`.trim(),
        email: profile.email
      },
      items: [
        {
          amount: Math.round(financial.total * 100), // Convert to cents
          description: `Reserva de espaço - ${booking.room_name || 'Espaço'}`,
          quantity: 1
        }
      ],
      payments: [
        {
          payment_method: 'credit_card',
          credit_card: {
            installments: parseInt(installments),
            statement_descriptor: 'LETCLINIC',
            ...(financial.card_id && { card_id: financial.card_id }),
            ...(financial.token_card && { card_token: financial.token_card })
          }
        }
      ]
    };

    return api.createOrderV5(order);
  },

  /**
   * Process payment using V5 transaction compatibility endpoint
   * @param {Object} paymentData - Payment data
   */
  processPayment: async (paymentData) => {
    // FIXED: Use the payBooking method that's already been fixed to call booker-room endpoint
    return api.payBooking(paymentData);
  },

  /**
   * Check payment status using V5 API
   * @param {string} transactionId - Transaction ID
   */
  checkPaymentStatus: async (transactionId) => {
    return api.getTransactionV5(transactionId);
  },

  /**
   * Capture charge (for pre-authorized payments)
   * @param {string} chargeId - Charge ID
   * @param {number} amount - Amount to capture in cents
   * @param {string} code - Authorization code
   */
  captureCharge: async (chargeId, amount, code) => {
    return api.captureChargeV5(chargeId, { amount, code });
  },

  /**
   * Refund charge
   * @param {string} chargeId - Charge ID
   * @param {number} amount - Amount to refund in cents
   * @param {Object} bankAccount - Bank account for refund (optional)
   */
  refundCharge: async (chargeId, amount, bankAccount = null) => {
    const refundData = { amount };
    if (bankAccount) {
      refundData.bank_account = bankAccount;
    }
    return api.refundChargeV5(chargeId, refundData);
  }
};
